(() => {
  'use strict';

  const EVENT_NAME = 'mrpoint:apply-use-point';
  const INSTALLED_KEY = '__mrPointCopyHelperInstalled';
  const PENDING_DISCOUNT_KEY = 'mrPointPendingDiscount';
  const PENDING_DISCOUNT_TTL_MS = 24 * 60 * 60 * 1000;
  const POINT_CLEAR_TEXT = '使用ポイントをクリアする';
  const POINT_CLEAR_SELECTOR = '.js-reset-button, .mrpoint-section__reset-button';
  const HELPER_CLASS = 'mrpoint-copy-helper';
  const HELPER_STYLE_ID = 'mrpoint-copy-helper-style';
  const HELPER_TARGETS = [
    {
      key: 'main-cart',
      selector: '#main-cart-footer .cart__blocks',
      before: '#main-cart-footer .cart__ctas',
    },
    {
      key: 'cart-drawer',
      selector: 'cart-drawer .drawer__footer',
      before: 'cart-drawer .cart__ctas',
    },
    {
      key: 'cart-notification',
      selector: '#cart-notification-form',
      before: 'button[name="checkout"]',
    },
  ];

  if (window[INSTALLED_KEY]) return;
  window[INSTALLED_KEY] = true;

  let activeDiscount = null;
  let renderTimer = null;

  const readStorageItem = (key) => {
    try {
      return JSON.parse(sessionStorage.getItem(key) || 'null');
    } catch (_) {
      return null;
    }
  };

  const writeStorageItem = (key, value) => {
    try {
      sessionStorage.setItem(key, JSON.stringify(value));
      return true;
    } catch (_) {
      return false;
    }
  };

  const removeStorageItem = (key) => {
    try {
      sessionStorage.removeItem(key);
    } catch (_) {
      // 削除できない場合も、表示はメモリ上の状態で更新します。
    }
  };

  const now = () => Date.now();

  const isExpired = (timestamp) =>
    !timestamp || now() - timestamp > PENDING_DISCOUNT_TTL_MS;

  const normalizeText = (text) => String(text || '').replace(/\s+/g, '');

  const getStoredDiscountState = () => {
    const discount = activeDiscount?.couponCode
      ? activeDiscount
      : readStorageItem(PENDING_DISCOUNT_KEY);

    if (!discount?.couponCode) {
      return { status: 'missing', discount: null };
    }

    if (isExpired(discount.timestamp)) {
      return { status: 'expired', discount };
    }

    activeDiscount = discount;
    return { status: 'valid', discount };
  };

  const getActiveDiscount = () => {
    const state = getStoredDiscountState();

    if (state.status === 'valid') return state.discount;

    if (state.status === 'expired') {
      activeDiscount = null;
      removeStorageItem(PENDING_DISCOUNT_KEY);
    }

    return null;
  };

  const saveDiscount = ({ couponCode, point }) => {
    activeDiscount = {
      couponCode,
      point,
      timestamp: now(),
    };

    writeStorageItem(PENDING_DISCOUNT_KEY, activeDiscount);
  };

  const clearDiscount = () => {
    activeDiscount = null;
    removeStorageItem(PENDING_DISCOUNT_KEY);
    renderHelpers();
  };

  const isPointClearTrigger = (target) => {
    const trigger = target?.closest?.(
      `${POINT_CLEAR_SELECTOR}, button, a, [role="button"]`
    );

    return Boolean(
      trigger?.matches?.(POINT_CLEAR_SELECTOR) ||
        normalizeText(trigger?.textContent).includes(normalizeText(POINT_CLEAR_TEXT))
    );
  };

  const cleanupLegacyDiscountInputs = () => {
    document
      .querySelectorAll('[data-mrpoint-auto-discount="true"]')
      .forEach((input) => input.remove());

    document
      .querySelectorAll('[name="discount"][data-mrpoint-auto-filled="true"]')
      .forEach((input) => {
        input.value = '';
        delete input.dataset.mrpointAutoFilled;
      });
  };

  const ensureStyle = () => {
    if (document.getElementById(HELPER_STYLE_ID)) return;

    const style = document.createElement('style');
    style.id = HELPER_STYLE_ID;
    style.textContent = `
      .${HELPER_CLASS} {
        border: 1px solid rgba(var(--color-foreground), 0.18);
        border-radius: 4px;
        padding: 1.4rem;
        margin: 1.6rem 0;
        background: rgba(var(--color-foreground), 0.035);
        color: rgb(var(--color-foreground));
      }

      .${HELPER_CLASS}[hidden] {
        display: none !important;
      }

      .${HELPER_CLASS}__label {
        display: block;
        margin-bottom: 0.7rem;
        font-weight: 700;
        line-height: 1.4;
      }

      .${HELPER_CLASS}__row {
        display: flex;
        gap: 0.8rem;
        align-items: stretch;
      }

      .${HELPER_CLASS}__code {
        flex: 1 1 auto;
        min-width: 0;
        border: 1px solid rgba(var(--color-foreground), 0.35);
        border-radius: 4px;
        padding: 0.9rem 1rem;
        background: rgb(var(--color-background));
        color: rgb(var(--color-foreground));
        font: inherit;
        font-size: 1.4rem;
      }

      .${HELPER_CLASS}__copy {
        flex: 0 0 auto;
        min-width: 7.2rem;
        border-radius: 4px;
        padding: 0.9rem 1.2rem;
        min-height: 4.4rem;
      }

      .${HELPER_CLASS}__note,
      .${HELPER_CLASS}__status {
        margin: 0.8rem 0 0;
        font-size: 1.3rem;
        line-height: 1.5;
      }

      .${HELPER_CLASS}__status {
        min-height: 1.95rem;
        font-weight: 700;
      }

      @media screen and (max-width: 749px) {
        .${HELPER_CLASS}__row {
          flex-direction: column;
        }

        .${HELPER_CLASS}__copy {
          width: 100%;
        }
      }
    `;
    document.head.appendChild(style);
  };

  const createHelper = (key) => {
    const wrapper = document.createElement('div');
    wrapper.className = HELPER_CLASS;
    wrapper.dataset.mrpointCopyHelper = key;
    wrapper.hidden = true;
    wrapper.innerHTML = `
      <p class="${HELPER_CLASS}__label">ポイント割引用コード</p>
      <div class="${HELPER_CLASS}__row">
        <input class="${HELPER_CLASS}__code" type="text" readonly aria-label="ポイント割引用コード">
        <button class="${HELPER_CLASS}__copy button button--secondary" type="button">コピー</button>
      </div>
      <p class="${HELPER_CLASS}__note">チェックアウト画面のクーポンコード欄に貼り付けてください。</p>
      <p class="${HELPER_CLASS}__status" role="status" aria-live="polite"></p>
    `;
    return wrapper;
  };

  const placeHelper = ({ key, selector, before }) => {
    const container = document.querySelector(selector);
    if (!container) return null;

    let helper = container.querySelector(`[data-mrpoint-copy-helper="${key}"]`);
    if (helper) return helper;

    helper = createHelper(key);
    const beforeElement = before ? document.querySelector(before) : null;

    if (beforeElement?.parentElement === container) {
      container.insertBefore(helper, beforeElement);
    } else {
      container.appendChild(helper);
    }

    return helper;
  };

  const updateHelper = (helper, discount) => {
    if (!helper) return;

    const codeInput = helper.querySelector(`.${HELPER_CLASS}__code`);
    const status = helper.querySelector(`.${HELPER_CLASS}__status`);
    const couponCode = String(discount?.couponCode || '').trim();

    if (!couponCode) {
      helper.hidden = true;
      if (codeInput) codeInput.value = '';
      if (status) status.textContent = '';
      return;
    }

    helper.hidden = false;
    if (codeInput && codeInput.value !== couponCode) {
      codeInput.value = couponCode;
    }
    if (status?.dataset.couponCode !== couponCode) {
      status.textContent = '';
      status.dataset.couponCode = couponCode;
    }
  };

  const renderHelpers = () => {
    cleanupLegacyDiscountInputs();
    ensureStyle();

    const discount = getActiveDiscount();

    HELPER_TARGETS.forEach((target) => {
      updateHelper(placeHelper(target), discount);
    });
  };

  const scheduleRender = () => {
    if (renderTimer) return;

    renderTimer = window.setTimeout(() => {
      renderTimer = null;
      renderHelpers();
    }, 50);
  };

  const copyText = async (text) => {
    if (navigator.clipboard?.writeText) {
      await navigator.clipboard.writeText(text);
      return;
    }

    const textarea = document.createElement('textarea');
    textarea.value = text;
    textarea.setAttribute('readonly', '');
    textarea.style.position = 'fixed';
    textarea.style.top = '-9999px';
    document.body.appendChild(textarea);
    textarea.select();
    document.execCommand('copy');
    textarea.remove();
  };

  document.addEventListener(EVENT_NAME, (event) => {
    const couponCode = String(event.detail?.couponCode || '').trim();
    const point = String(event.detail?.point || '').trim();

    if (!couponCode) {
      console.warn('[MR.POINT] couponCodeが取得できませんでした。', event.detail);
      return;
    }

    saveDiscount({ couponCode, point });
    renderHelpers();
  });

  document.addEventListener(
    'click',
    async (event) => {
      if (isPointClearTrigger(event.target)) {
        clearDiscount();
        return;
      }

      const copyButton = event.target?.closest?.(`.${HELPER_CLASS}__copy`);
      if (!copyButton) return;

      const helper = copyButton.closest(`.${HELPER_CLASS}`);
      const codeInput = helper?.querySelector(`.${HELPER_CLASS}__code`);
      const status = helper?.querySelector(`.${HELPER_CLASS}__status`);
      const couponCode = String(codeInput?.value || '').trim();

      if (!couponCode) return;

      try {
        await copyText(couponCode);
        if (status) status.textContent = 'コピーしました';
      } catch (error) {
        if (status) status.textContent = 'コピーできませんでした';
        console.error('[MR.POINT] コードのコピーに失敗しました。', error);
      }
    },
    true
  );

  const init = () => {
    renderHelpers();

    const observer = new MutationObserver(scheduleRender);
    observer.observe(document.documentElement, {
      childList: true,
      subtree: true,
    });
  };

  window.addEventListener('pageshow', renderHelpers);

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init, { once: true });
  } else {
    init();
  }
})();
